/**
 * El adjunto del Despacho (2026-10-06): adjuntar un documento es decirle
 * algo largo al asesor. Dos pasos, como la nota de voz:
 *
 * - `read`: el cuerpo es el FICHERO CRUDO, con su `Content-Type` y el
 *   nombre en `?name=`; vuelve el texto leído o por qué no se puede leer.
 *   El fichero no se guarda: se lee y se tira.
 * - el texto viaja después dentro del mensaje (`SendMessageRequest.
 *   attachment`) y queda en la conversación como parte de lo que dijiste.
 *
 * Que un documento no se pueda leer NO es un error del canal: es un
 * desenlace previsto (`status: "rejected"`, HTTP 200) con su causa. Los
 * errores HTTP son los de siempre: `session` 401 · `too-large` 413 (pasa
 * de `ATTACHMENT_LIMITS.fileBytes`) · `invalid` 400 (sin cuerpo o sin
 * nombre).
 */
import { z } from "zod";

export const ATTACHMENT_ROUTES = {
  read: "/despacho/attachments/read",
} as const;

export const ATTACHMENT_LIMITS = {
  /** 15 MB de fichero: de sobra para 20 páginas con texto. */
  fileBytes: 15 * 1024 * 1024,
  /** Páginas que se leen (decisión de Martí, 2026-10-06; se ajusta con el uso). */
  pages: 20,
  /** Lo que cuenta como una página en un documento sin páginas (Word, texto). */
  charsPerPage: 3000,
  /** Techo del texto que cabe en un mensaje, tenga las páginas que tenga. */
  textChars: 90_000,
  nameChars: 160,
} as const;

/** Lo que se lee: PDF con texto, Word (.docx) y texto. Lo decide la extensión; el tipo MIME del navegador no es de fiar. */
export const ATTACHMENT_EXTENSIONS = ["pdf", "docx", "txt", "md"] as const;
export type AttachmentExtension = (typeof ATTACHMENT_EXTENSIONS)[number];
/** Para el `accept` del selector de ficheros. */
export const ATTACHMENT_ACCEPT = ".pdf,.docx,.txt,.md";

/**
 * Por qué no se lee un documento:
 * `figures` hoja de cálculo o documento que es casi todo tabla de cifras
 * (las cifras entran por el puerto de conexiones, R-8) · `scanned` no trae
 * texto (escaneado, imagen) · `too-long` pasa de `pages` (lleva cuántas
 * tiene) · `format` otro tipo de fichero · `unreadable` protegido o
 * dañado · `too-big` pasa de `fileBytes` (lo dice el cliente antes de
 * subir, y el 413 de la ruta).
 */
export const AttachmentRejection = z.enum(["figures", "scanned", "too-long", "format", "unreadable", "too-big"]);
export type AttachmentRejection = z.infer<typeof AttachmentRejection>;

/** Un documento leído: lo que el asesor oirá. */
export const ReadAttachment = z.object({
  name: z.string().min(1).max(ATTACHMENT_LIMITS.nameChars),
  pages: z.number().int().min(1).max(ATTACHMENT_LIMITS.pages),
  text: z.string().min(1).max(ATTACHMENT_LIMITS.textChars),
});
export type ReadAttachment = z.infer<typeof ReadAttachment>;

export const ReadAttachmentOutcome = z.discriminatedUnion("status", [
  ReadAttachment.extend({ status: z.literal("ok") }),
  z.object({ status: z.literal("rejected"), reason: AttachmentRejection, /** Solo en `too-long`. */ pages: z.number().int().optional() }),
]);
export type ReadAttachmentOutcome = z.infer<typeof ReadAttachmentOutcome>;

/** El documento tal y como se enseña en un turno ya enviado: sin su texto. */
export const TurnAttachment = z.object({ name: z.string(), pages: z.number().int() });
export type TurnAttachment = z.infer<typeof TurnAttachment>;
